import React, { useEffect, Suspense, lazy } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider } from "@/context/AuthContext";
import { ProtectedRoute, RoleRedirect } from "@/components/ProtectedRoute";
import { AppLayout } from "@/components/layout/AppLayout";
import { AdminCodeGate } from "@/components/AdminCodeGate";
import OfflineBanner from "@/components/OfflineBanner";
import OfflineSync from "@/components/OfflineSync";
import VideoQueueBanner from "@/components/VideoQueueBanner";
import PendingVideoBanner from "@/components/PendingVideoBanner"; // eslint-disable-line no-unused-vars

// Landing/auth stays eager (needed immediately)
import LoginPage from "@/pages/LoginPage";

// Everything else is lazy — kills the initial bundle size and post-login TTI.
const EmployeeDashboard = lazy(() => import("@/pages/employee/EmployeeDashboard"));
const CleaningDetailPage = lazy(() => import("@/pages/employee/CleaningDetailPage"));
const NewReportPage = lazy(() => import("@/pages/employee/NewReportPage"));
const ReportsListPage = lazy(() => import("@/pages/employee/ReportsListPage"));
const CleaningsListPage = lazy(() => import("@/pages/employee/CleaningsListPage"));
const ProfilePage = lazy(() => import("@/pages/employee/ProfilePage"));
const NotificationsPage = lazy(() => import("@/pages/employee/NotificationsPage"));

const AdminDashboard = lazy(() => import("@/pages/admin/AdminDashboard"));
const AdminHomePage = lazy(() => import("@/pages/admin/AdminHomePage"));
const UnderConstructionPage = lazy(() => import("@/pages/admin/UnderConstructionPage"));
const WarehouseStaffPage = lazy(() => import("@/pages/admin/WarehouseStaffPage"));
const AdminSchedulePage = lazy(() => import("@/pages/admin/AdminSchedulePage"));
const ProgrammingPage = lazy(() => import("@/pages/admin/ProgrammingPage"));
const RestDaysPage = lazy(() => import("@/pages/admin/RestDaysPage"));
const SchedulePrintPage = lazy(() => import("@/pages/admin/SchedulePrintPage"));
const AdminStructuresPage = lazy(() => import("@/pages/admin/AdminStructuresPage"));
const AdminUsersPage = lazy(() => import("@/pages/admin/AdminUsersPage"));
const EconomicsPage = lazy(() => import("@/pages/admin/EconomicsPage"));
const TaskListPage = lazy(() => import("@/pages/admin/TaskListPage"));
const ManagementPage = lazy(() => import("@/pages/admin/ManagementPage"));
const ReturnsSheetPage = lazy(() => import("@/pages/admin/ReturnsSheetPage"));
const SpecificNotesPage = lazy(() => import("@/pages/admin/SpecificNotesPage"));
const SeasonSwapsPage = lazy(() => import("@/pages/admin/SeasonSwapsPage"));
const ClientDataPage = lazy(() => import("@/pages/public/ClientDataPage"));
const ReturnsPage = lazy(() => import("@/pages/admin/ReturnsPage"));
const NonConformiHubPage = lazy(() => import("@/pages/admin/nonconformi/NonConformiHubPage"));
const NonConformiRegistryPage = lazy(() => import("@/pages/admin/nonconformi/NonConformiRegistryPage"));
const StorniAnalyticsPage = lazy(() => import("@/pages/admin/StorniAnalyticsPage"));
const AdminDdtPage = lazy(() => import("@/pages/admin/AdminDdtPage"));
const AdminVideoStatusPage = lazy(() => import("@/pages/admin/AdminVideoStatusPage"));
const AdminStandardsPage = lazy(() => import("@/pages/admin/AdminStandardsPage"));
const AdminSetupPage = lazy(() => import("@/pages/admin/AdminSetupPage"));
const TrashPage = lazy(() => import("@/pages/admin/TrashPage"));
const BookingsPage = lazy(() => import("@/pages/admin/BookingsPage"));
const StaffQualityPage = lazy(() => import("@/pages/admin/StaffQualityPage"));
const StaffHoursPage = lazy(() => import("@/pages/admin/StaffHoursPage"));
const AdminCompanyPage = lazy(() => import("@/pages/admin/AdminCompanyPage"));
const TimesReportPage = lazy(() => import("@/pages/admin/TimesReportPage"));
const CityTaxPage = lazy(() => import("@/pages/admin/CityTaxPage"));
const MonthlyClosePage = lazy(() => import("@/pages/admin/MonthlyClosePage"));
const BillingPage = lazy(() => import("@/pages/admin/BillingPage"));
const DayHistoryPage = lazy(() => import("@/pages/admin/DayHistoryPage"));
const SuppliesPage = lazy(() => import("@/pages/admin/SuppliesPage"));
const AdminReportsPage = lazy(() => import("@/pages/admin/AdminReportsPage"));
const DailyAssignmentSheet = lazy(() => import("@/pages/admin/DailyAssignmentSheet"));
const AdminProductsPage = lazy(() => import("@/pages/admin/AdminProductsPage"));
const AdminKitsPage = lazy(() => import("@/pages/admin/AdminKitsPage"));
const AccessSearchPage = lazy(() => import("@/pages/admin/AccessSearchPage"));
const RoutePlannerPage = lazy(() => import("@/pages/admin/RoutePlannerPage"));
const HistoryPage = lazy(() => import("@/pages/admin/HistoryPage"));
const AdminClientsPage = lazy(() => import("@/pages/admin/AdminClientsPage"));
const HqWarehousePage = lazy(() => import("@/pages/admin/HqWarehousePage"));
const DeliverySheetsPage = lazy(() => import("@/pages/admin/DeliverySheetsPage"));
const LogisticsPrintPage = lazy(() => import("@/pages/admin/LogisticsPrintPage"));
const ClientPortalPage = lazy(() => import("@/pages/client/ClientPortalPage"));
const KiloPage = lazy(() => import("@/pages/KiloPage"));
const PrivateLaundryPage = lazy(() => import("@/pages/admin/PrivateLaundryPage"));
const LogisticsPage = lazy(() => import("@/pages/logistics/LogisticsPage"));
const LogisticsStructuresPage = lazy(() => import("@/pages/logistics/LogisticsStructuresPage"));
const AdminInventoryOverview = lazy(() => import("@/pages/admin/AdminInventoryPages").then(m => ({ default: m.AdminInventoryOverview })));
const AdminStructureInventory = lazy(() => import("@/pages/admin/AdminInventoryPages").then(m => ({ default: m.AdminStructureInventory })));

const EMPLOYEE_ROLES = ["dipendente", "responsabile"];
const MANAGER_ROLES = ["super_admin", "responsabile"];
const LOGISTICS_ROLES = ["super_admin", "resp_logistica", "rider"];
const SUPER_ONLY = ["super_admin"];

function RouteFallback() {
  return (
    <div className="p-8 flex items-center justify-center min-h-[40vh]">
      <div className="h-8 w-8 border-2 border-[#EC4E5B] border-t-transparent rounded-full animate-spin" />
    </div>
  );
}

function App() {
  useEffect(() => {
    if ("serviceWorker" in navigator && window.location.protocol === "https:") {
      navigator.serviceWorker.register("/service-worker.js").catch(() => {});
    }
  }, []);

  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <OfflineBanner />
          <OfflineSync />
          <VideoQueueBanner />
          {/* Avviso «video in attesa» disattivato su richiesta */}
          <Suspense fallback={<RouteFallback />}>
            <Routes>
              <Route path="/login" element={<LoginPage />} />
              <Route path="/dati-cliente/:token" element={<ClientDataPage />} />

              {/* Employee interface (dipendente + responsabile) */}
              <Route
                element={
                  <ProtectedRoute roles={EMPLOYEE_ROLES}>
                    <AppLayout variant="employee" />
                  </ProtectedRoute>
                }
              >
                <Route path="/oggi" element={<EmployeeDashboard />} />
                <Route path="/pulizie" element={<CleaningsListPage />} />
                <Route path="/pulizie/:id" element={<CleaningDetailPage />} />
                <Route path="/pulizie/:id/segnala" element={<NewReportPage />} />
                <Route path="/segnalazioni" element={<ReportsListPage />} />
                <Route path="/segnalazioni/nuova" element={<NewReportPage />} />
                <Route path="/kilo" element={<KiloPage />} />
              </Route>

              {/* Rotte comuni: accessibili a tutti i ruoli operativi non-super_admin */}
              <Route
                element={
                  <ProtectedRoute roles={["dipendente", "responsabile", "resp_logistica"]}>
                    <AppLayout variant="employee" />
                  </ProtectedRoute>
                }
              >
                <Route path="/notifiche" element={<NotificationsPage />} />
                <Route path="/profilo" element={<ProfilePage />} />
              </Route>

              <Route path="/" element={<RoleRedirect />} />

              <Route
                path="/cliente"
                element={
                  <ProtectedRoute roles={["cliente"]}>
                    <ClientPortalPage />
                  </ProtectedRoute>
                }
              />

              {/* Manager overview pages (super_admin + responsabile) */}
              <Route
                element={
                  <ProtectedRoute roles={MANAGER_ROLES}>
                    <AppLayout variant="admin" />
                  </ProtectedRoute>
                }
              >
                <Route path="/admin/schedule" element={<AdminSchedulePage />} />
                <Route path="/admin/programmazione" element={<ProgrammingPage />} />
                <Route path="/admin/programmazione/pulizie" element={<ProgrammingPage mode="cleanings" />} />
                <Route path="/admin/programmazione/consegne" element={<ProgrammingPage mode="deliveries" />} />
                <Route path="/admin/magazzino/personale" element={<WarehouseStaffPage />} />
                <Route path="/admin/gestione" element={<ManagementPage />} />
                <Route path="/admin/rientri" element={<ReturnsSheetPage />} />
                <Route path="/admin/riposi" element={<AdminCodeGate><RestDaysPage /></AdminCodeGate>} />
                <Route path="/admin/schedule/print" element={<SchedulePrintPage />} />
                <Route path="/admin/reports" element={<AdminReportsPage />} />
                <Route path="/admin/inventory/:id" element={<AdminStructureInventory />} />
                <Route path="/admin" element={<AdminDashboard />} />
                <Route path="/admin/home" element={<AdminHomePage />} />
                <Route path="/admin/home/:area" element={<AdminHomePage />} />
                <Route path="/admin/home/:area/:group" element={<AdminHomePage />} />
                <Route path="/admin/in-costruzione/:key" element={<UnderConstructionPage />} />
                <Route path="/admin/storico" element={<HistoryPage />} />
                <Route path="/admin/clienti" element={<AdminCodeGate><AdminClientsPage /></AdminCodeGate>} />
              </Route>

              {/* Super-admin-only routes */}
              <Route
                element={
                  <ProtectedRoute roles={SUPER_ONLY}>
                    <AppLayout variant="admin" />
                  </ProtectedRoute>
                }
              >
                <Route path="/admin/structures" element={<AdminStructuresPage />} />
                <Route path="/admin/inventory" element={<AdminInventoryOverview />} />
                <Route path="/admin/magazzino-sede" element={<HqWarehousePage />} />
                <Route path="/admin/movements" element={<Navigate to="/admin/storico?tab=movimenti" replace />} />
                <Route path="/admin/scheda/:userId/:date" element={<DailyAssignmentSheet />} />
                <Route path="/admin/products" element={<AdminProductsPage />} />
                <Route path="/admin/kits" element={<AdminKitsPage />} />
                <Route path="/admin/access-search" element={<AccessSearchPage />} />
                <Route path="/admin/deliveries" element={<Navigate to="/logistica" replace />} />
                <Route path="/admin/magazzino-pulizie" element={<Navigate to="/admin/products" replace />} />
                <Route path="/admin/logistica/storico" element={<Navigate to="/admin/storico?tab=consegne" replace />} />
                <Route path="/admin/scostamenti" element={<Navigate to="/admin" replace />} />
                <Route path="/admin/giri" element={<RoutePlannerPage />} />
                <Route path="/admin/logistica/schede" element={<DeliverySheetsPage />} />
                <Route path="/admin/logistica/stampa" element={<LogisticsPrintPage />} />
                <Route path="/admin/kilo" element={<KiloPage />} />
                <Route path="/admin/users" element={<AdminCodeGate><AdminUsersPage /></AdminCodeGate>} />
                <Route path="/admin/economics" element={<AdminCodeGate><EconomicsPage /></AdminCodeGate>} />
                <Route path="/admin/tasklist" element={<TaskListPage />} />
                <Route path="/admin/note-specifiche" element={<SpecificNotesPage />} />
                <Route path="/admin/stagioni" element={<SeasonSwapsPage />} />
                <Route path="/admin/storni" element={<Navigate to="/admin/non-conformi" replace />} />
                <Route path="/admin/non-conformi" element={<NonConformiHubPage />} />
                <Route path="/admin/non-conformi/gestione" element={<ReturnsPage />} />
                <Route path="/admin/non-conformi/registro" element={<NonConformiRegistryPage />} />
                <Route path="/admin/non-conformi/pulizia" element={<Navigate to="/admin/non-conformi" replace />} />
                <Route path="/admin/non-conformi/magazzinetto" element={<Navigate to="/admin/non-conformi" replace />} />
                <Route path="/admin/analisi-storni" element={<StorniAnalyticsPage />} />
                <Route path="/admin/ddt" element={<AdminDdtPage />} />
                <Route path="/admin/stato-video" element={<AdminVideoStatusPage />} />
                <Route path="/admin/standard" element={<AdminCodeGate><AdminStandardsPage /></AdminCodeGate>} />
                <Route path="/admin/pulizie/:id" element={<CleaningDetailPage />} />
                <Route path="/admin/setup" element={<AdminSetupPage />} />
                <Route path="/admin/cestino" element={<TrashPage />} />
                <Route path="/admin/prenotazioni" element={<BookingsPage />} />
                <Route path="/admin/storni-dipendenti" element={<StaffQualityPage />} />
                <Route path="/admin/ore-dipendenti" element={<AdminCodeGate><StaffHoursPage /></AdminCodeGate>} />
                <Route path="/admin/azienda" element={<AdminCompanyPage />} />
                <Route path="/admin/report-tempi" element={<AdminCodeGate><TimesReportPage /></AdminCodeGate>} />
                <Route path="/admin/chiusura-mese" element={<AdminCodeGate><MonthlyClosePage /></AdminCodeGate>} />
                <Route path="/admin/fatturazione" element={<AdminCodeGate><BillingPage /></AdminCodeGate>} />
                <Route path="/admin/storico-giorno" element={<DayHistoryPage />} />
                <Route path="/admin/tassa-soggiorno" element={<CityTaxPage />} />
                <Route path="/admin/materiale" element={<SuppliesPage />} />
                <Route path="/admin/lavanderia" element={<PrivateLaundryPage />} />
              </Route>

              {/* Logistics routes: Super Admin + Responsabile Logistica */}
              <Route
                element={
                  <ProtectedRoute roles={LOGISTICS_ROLES}>
                    <AppLayout variant="employee" />
                  </ProtectedRoute>
                }
              >
                <Route path="/logistica" element={<LogisticsPage />} />
                <Route path="/logistica/strutture" element={<LogisticsStructuresPage />} />
              </Route>

              <Route path="*" element={<RoleRedirect />} />
            </Routes>
          </Suspense>
          <Toaster position="bottom-right" richColors closeButton />
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
